import React, { Fragment } from 'react'
import { connect } from 'react-redux'
import styles from './Dashboard.scss'
import { StatsWidget, getCareerStats } from '../StatsWidget'
import { RecentActivity } from './RecentActivity'
import { MiniFeed } from './MiniFeed'
import { Goals } from './Goals'
import { Events } from './Events'
import { Tabs } from './Tabs'
import { TrialNoticeBar } from './TrialNoticeBar'
import { DashboardOnboardingOverlay } from './DashboardOnboardingOverlay'
import { EmptyDashboard } from './EmptyDashboard'
import { dashboardMessages } from './intlMessages'
import { CurrentChallenges } from '../Challenges/DashboardCards'
import * as userActions from 'lib/api/currentUser'
import * as goalsActions from 'lib/api/goalsForUser'
import * as favoritesActions from 'lib/api/favorites'
import { apiv3 } from 'lib/apiv3'
import { Msg, featureNames, commonNouns, commonVerbs, commonCTAs } from 'lib/localization'
import { Loadable } from 'common/Loadable'
import { Icon } from 'common/Icon'
import { ColWrap, MainCol, SecondaryCol } from 'common/layouts/SideNavLayout/TwoCol'
import { HeroCard } from 'common/styleguide/HeroCard'
import { IeNudgeBar } from 'common/banners/IeNudgeBar'
import { MobileCollapse } from 'common/layouts/SideNavLayout/SideNavContexts'
import { Card } from 'common/styleguide/Card'
import { AccountTypeBadge } from 'common/AccountTypeBadge'
import Helmet from 'react-helmet'
import { FormattedMessage as T, injectIntl } from 'react-intl'
import _ from 'lodash'

import findBg from './img/find_bg.jpg'
import findBg2x from './img/find_bg@2x.jpg'

import planBg from './img/plan_bg.jpg'
import planBg2x from './img/plan_bg@2x.jpg'

import recordBg from './img/record_bg.jpg'
import recordBg2x from './img/record_bg@2x.jpg'

const MINI_ONBOARDING_PREF = 'show_dash_mini_onboarding'
const OVERLAY_ONBOARDING_PREF = 'show_dash_onboarding_overlay'

// exported for test purposes only
export class Dashboard extends React.PureComponent {
  componentDidMount () {
    window.showOnboarding = this.showOnboarding
  }

  dashboardIsEmpty = () => {
    try {
      const { goalsForUser, challengesForUser, loadableResources } = this.props
      const { recentTRoutes, currentUserEvents, feedItems, favorites } = loadableResources

      // A dashboard should show the fullscreen onboarding only if there are NONE
      // of the following items present
      // trips/routes
      // events
      // goals
      // challenges
      // feed items
      // pinned items
      const totalDisplayable = recentTRoutes.results.length +
        currentUserEvents.length +
        goalsForUser.size +
        challengesForUser.size +
        feedItems.length +
        favorites.value.results.length

      return totalDisplayable === 0
    } catch (e) {
      console.error('encountered an error attempting to read initial dashboard empty state:')
      console.error(e)
      console.error('falling back to normal dashboard render')
      return false
    }
  }

  dismissOnboarding = () => {
    const { setPreference } = this.props
    setPreference(MINI_ONBOARDING_PREF, false)
  }

  dismissOnboardingOverlay = () => {
    const { setPreference } = this.props
    setPreference(OVERLAY_ONBOARDING_PREF, false)
  }

  // TODO - figure out how to get this into the admin menu!
  showOnboarding = () => {
    const { setPreference } = this.props
    setPreference(MINI_ONBOARDING_PREF, true)
    setPreference(OVERLAY_ONBOARDING_PREF, true)
  }

  render () {
    const {
      clearTrialBanner,
      currentUser,
      goalsForUser,
      challengesForUser,
      hasChallengeParticipants,
      loadableResources,
      setPreference,
      intl
    } = this.props
    const { recentTRoutes, currentUserEvents, careerStats, feedItems, siteActivity } = loadableResources

    if (this.dashboardIsEmpty()) {
      return <EmptyDashboard />
    }

    const showOnboarding = !!currentUser.preferences.get(MINI_ONBOARDING_PREF, true)
    const showOnboardingOverlay = !!currentUser.preferences.get(OVERLAY_ONBOARDING_PREF, true)

    return (
      <Fragment>
        <Helmet title={`${intl.formatMessage(featureNames.dashboard)} · Ride with GPS`} />
        <TrialNoticeBar currentUser={currentUser} setPreference={setPreference} clearTrialBanner={clearTrialBanner} />
        <IeNudgeBar currentUser={currentUser} setPreference={setPreference} />
        <MobileCollapse>
          {showOnboardingOverlay && <DashboardOnboardingOverlay currentUser={currentUser} onRequestClose={this.dismissOnboardingOverlay} />}
          {showOnboarding && <MiniOnboarding onDismiss={this.dismissOnboarding} />}
        </MobileCollapse>
        <ColWrap>
          <MainCol>
            <h2 data-heading-alt><Msg m={commonNouns.stats} /></h2>
            <StatsWidget
              initialStats={careerStats}
              user={currentUser}
              persistInterval={(interval) => setPreference('default_career_interval', interval)} />
            <Tabs currentUser={currentUser} recentTRoutes={recentTRoutes} />
          </MainCol>
          <SecondaryCol>
            <SecondaryColContent
              accountLevel={currentUser.accountLevel}
              siteActivity={siteActivity}
              feedItems={feedItems}
              goalsForUser={goalsForUser}
              challengesForUser={challengesForUser}
              hasChallengeParticipants={hasChallengeParticipants}
              currentUserEvents={currentUserEvents} />
          </SecondaryCol>
        </ColWrap>
      </Fragment>
    )
  }
}

const SecondaryColContent = ({ accountLevel, siteActivity, feedItems, goalsForUser, challengesForUser, hasChallengeParticipants, currentUserEvents }) => {
  // lodash sortBy is stable, so we just need to order these by weight,
  // and sort by Math.min(contentCount, 1) in order to achieve the behavior specified in
  // https://github.com/ridewithgps/ridewithgps/issues/7127
  const modules = [
    {
      contentCount: goalsForUser.size,
      element: <Fragment key='goals'>
        <h2 data-heading-alt><Msg m={featureNames.goals} /></h2>
        <Goals goalParticipations={goalsForUser} />
      </Fragment>
    },
    {
      contentCount: feedItems.length,
      element: <Fragment key='minifeed'>
        <h2 data-heading-alt><T id='dashboard.miniFeed' defaultMessage='Feed' description="A view of a user's friends' activity" /></h2>
        <MiniFeed feedItems={feedItems} />
      </Fragment>
    },
    {
      contentCount: currentUserEvents.length,
      element: <Fragment key='events'>
        <h2 data-heading-alt><Msg m={featureNames.events} values={{ count: 0 }} /></h2>
        <Events currentUserEvents={currentUserEvents} />
      </Fragment>
    },
    {
      contentCount: siteActivity.length,
      element: <Fragment key='activity'>
        <h2 data-heading-alt>
          <T
            id='dashboard.recentActivity'
            defaultMessage='Recent Activity'
            description="Label for a list of the user's recent activity on the site" />
        </h2>
        <RecentActivity siteActivity={siteActivity} />
      </Fragment>
    }
  ]

  const sortedModules = _.sortBy(modules, m => -Math.min(m.contentCount, 1)).map(m => m.element)

  if (hasChallengeParticipants) {
    if (challengesForUser.size === 0) {
      sortedModules.push(
        <CurrentChallenges key='challenges' challenges={challengesForUser} />
      )
    } else {
      sortedModules.splice(0, 0,
        <CurrentChallenges key='challenges' challenges={challengesForUser} />
      )
    }
  }

  if (accountLevel === 0) {
    sortedModules.splice(1, 0, <div key='upgrade'>
      <h2 data-heading-alt>
        <Msg m={featureNames.upgrade} />
      </h2>
      <Card className={styles.premiumUpgrade}>
        <div className={styles.premiumTools}><AccountTypeBadge accountLevel={2} size={28} /><T id='Dashboard.premiumTools' defaultMessage='Premium Tools' /></div>
        <div className={styles.upgradeText}><T id='Dashboard.premiumUpgrade' defaultMessage='Gain access to powerful route planning tools and ride analysis features by upgrading to Premium.' /></div>
        <a href='/premium'><Msg m={commonCTAs.learnMorePremium} /></a>
      </Card>
    </div>)
  }

  return sortedModules
}

const MiniOnboarding = ({ onDismiss }) => (
  <div className={styles.MiniOnboarding}>
    <a className={styles.dismiss} onClick={onDismiss}><Msg capitalized m={commonVerbs.dismiss} /><Icon i='ios-close-circle-outline' size={24} /></a>
    <h2 data-heading-alt>
      <T
        id='dashboard.getStarted'
        defaultMessage='Get Started'
        description='A message introducing users to the site' />
    </h2>
    <div className={styles.cards}>
      <HeroCard href='/find' focalPoint='center 90%' img={findBg} img2x={findBg2x} scrimOpacity={[0.4, 0.4]} title={<Msg m={dashboardMessages.findRoutes} />} />
      <HeroCard href='/routes/new' img={planBg} img2x={planBg2x} scrimOpacity={[0.4, 0.4]} title={<Msg m={dashboardMessages.planARoute} />} />
      <HeroCard href='/app' img={recordBg} img2x={recordBg2x} scrimOpacity={[0.4, 0.4]} title={<Msg m={dashboardMessages.recordAndNavigate} />} />
    </div>
  </div>
)

const loadInitialResources = (currentUser, { loadGoalsForUser, loadUserFavorites }) => {
  const loadRecentTRoutes = () => apiv3.search({
    userId: currentUser.id,
    sortBy: 'created DESC',
    limit: 20,
    types: ['routes', 'trips']
  })

  const statsInterval = currentUser.preferences.get('default_career_interval') || 'career'
  const statsIntervalValue = StatsWidget.getIntervalValue(statsInterval, 0)
  const loadCareerStats = () => (
    getCareerStats(currentUser.id, statsInterval, statsIntervalValue)
  )

  // we currently don't have a way to exclude past events from the query, so
  // just filter client-side...
  const loadCurrentUserEvents = () => (
    apiv3.users.events(currentUser.id, { per_page: 100, exclude_past: true }).then(({ results }) => results)
  )

  const loadFeedItems = () => (
    apiv3.users.feedItems(currentUser.id, { per_page: 5, order_by: 'id', order_dir: 'desc' }).then(({ results }) => results)
  )

  const loadSiteActivity = () => (
    apiv3.users.siteActivity(currentUser.id, { per_page: 30, order_by: 'performed_at', order_dir: 'desc' }).then(({ results }) => results)
  )

  const loadFavorites = () => (
    loadUserFavorites(currentUser.id, { per_page: 5 })
  )

  const loadAll = Promise.all([
    loadRecentTRoutes(),
    loadCareerStats(),
    loadGoalsForUser(),
    loadCurrentUserEvents(),
    loadFeedItems(),
    loadSiteActivity(),
    loadFavorites()
  ])

  return loadAll.then(
    ([recentTRoutes, careerStats, _goalsForUser, currentUserEvents, feedItems, siteActivity, favorites]) => ({
      recentTRoutes,
      careerStats,
      currentUserEvents,
      feedItems,
      siteActivity,
      favorites
    })
  )
}

let lastFetchedResources = null
const loadOrReturnResourcesFromCacheThenRefetch = (currentUser, props, onResourceUpdate) => {
  // kick off a resource update in the background
  loadInitialResources(currentUser, props)
    .then(freshData => {
      lastFetchedResources = freshData
      onResourceUpdate(freshData)
    })

  // but if we've got cached data, return it immediately
  if (lastFetchedResources) {
    onResourceUpdate(lastFetchedResources)
  }
}

const LoadableDashboard = (props) => {
  return <Loadable
    {...props}
    component={Dashboard}
    loadingText={<T id='dashboard.loadableMessage' defaultMessage='Loading your dashboard' />}
    resourceLoaded={(onResourceUpdate) => loadOrReturnResourcesFromCacheThenRefetch(props.currentUser, props, onResourceUpdate)} />
}

const mapStateToProps = state => ({
  currentUser: state.currentUser,
  goalsForUser: state.goalsForUser.get('records'),
  challengesForUser: state.goalsForUser.get('challenges'),
  hasChallengeParticipants: state.goalsForUser.get('hasChallengeParticipants')
})

const mapDispatchToProps = {
  setPreference: userActions.setPreference,
  clearTrialBanner: userActions.clearTrialBanner,
  loadGoalsForUser: goalsActions.loadGoalsForUser,
  loadUserFavorites: favoritesActions.loadUserFavorites
}

export const DashboardContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(LoadableDashboard))
